/**
 * @fileoverview useModalScrollLock - Prevents background scrolling when modals are open.
 *
 * This hook addresses a common mobile UX issue where the background content
 * scrolls when a user tries to scroll within a modal (scroll bleed-through).
 *
 * Features:
 * - Locks body scroll when modal is open
 * - Preserves scroll position when locking/unlocking
 * - Handles multiple modals (ref counting)
 * - Cleans up properly on unmount
 *
 * @module hooks/useModalScrollLock
 */

import { useEffect, useRef } from 'react';

// Track how many modals are currently open (for nested modals)
let openModalCount = 0;
let originalOverflow = '';
let originalPosition = '';
let originalTop = '';
let originalWidth = '';
let scrollY = 0;

/**
 * Hook to lock body scroll when a modal is open.
 *
 * @param {boolean} isOpen - Whether the modal is currently open
 *
 * @example
 * function MyModal({ isOpen, onClose }) {
 *     useModalScrollLock(isOpen);
 *     if (!isOpen) return null;
 *     return <div className="modal">...</div>;
 * }
 */
export const useModalScrollLock = (isOpen) => {
    const wasOpen = useRef(false);

    useEffect(() => {
        // Only act on changes (open -> close or close -> open)
        if (isOpen && !wasOpen.current) {
            // Opening modal
            openModalCount++;

            if (openModalCount === 1) {
                // First modal opening - lock the body
                scrollY = window.scrollY;
                originalOverflow = document.body.style.overflow;
                originalPosition = document.body.style.position;
                originalTop = document.body.style.top;
                originalWidth = document.body.style.width;

                // Lock body in place
                document.body.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollY}px`;
                document.body.style.width = '100%';
            }

            wasOpen.current = true;
        } else if (!isOpen && wasOpen.current) {
            // Closing modal
            openModalCount = Math.max(0, openModalCount - 1);

            if (openModalCount === 0) {
                // Last modal closing - restore body scroll
                document.body.style.overflow = originalOverflow;
                document.body.style.position = originalPosition;
                document.body.style.top = originalTop;
                document.body.style.width = originalWidth;

                // Restore scroll position
                window.scrollTo(0, scrollY);
            }

            wasOpen.current = false;
        }

        // Cleanup on unmount
        return () => {
            if (wasOpen.current) {
                openModalCount = Math.max(0, openModalCount - 1);

                if (openModalCount === 0) {
                    document.body.style.overflow = originalOverflow;
                    document.body.style.position = originalPosition;
                    document.body.style.top = originalTop;
                    document.body.style.width = originalWidth;
                    window.scrollTo(0, scrollY);
                }

                wasOpen.current = false;
            }
        };
    }, [isOpen]);
};

export default useModalScrollLock;
